/**
 * Cargo Count Feature Types
 * Data structures for cargo volume tracking across geographic areas and time periods
 */

/**
 * Cargo count data returned from API
 */
export interface CargoCountResult {
  area: string; // Geographic area: "AG", "SAM", "USG", "WAF"
  period: string; // Time period: "1-10 Nov", "11-20 Nov", etc.
  fixed: number; // Confirmed/fixed cargo count
  expected: number; // Expected/anticipated cargo count
  average: number; // Historical average for comparison
}

/**
 * Summary statistics for a specific area
 */
export interface AreaSummary {
  area: string;
  totalFixed: number;
  totalExpected: number;
  totalAverage: number;
  color: string; // UI color for the area
}

/**
 * Filter options for cargo count data
 */
export interface CargoCountFilters {
  selectedAreas: string[]; // Array of area codes, empty = all areas
  periodRange: 'last7' | 'last30' | 'last90' | 'all';
}

/**
 * Zustand store state
 */
export interface CargoCountState {
  // Data
  data: CargoCountResult[];
  areaSummaries: AreaSummary[];

  // Filters
  filters: CargoCountFilters;

  // UI State
  loading: boolean;
  error: string | null;

  // Actions
  fetchCargoCount: () => Promise<void>;
  setFilters: (filters: Partial<CargoCountFilters>) => void;
  getFilteredData: () => CargoCountResult[];
  getAreaSummaries: () => AreaSummary[];
  refreshData: () => Promise<void>;
}

/**
 * Area color mapping for consistent UI
 */
export const AREA_COLORS: Record<string, string> = {
  AG: '#3b82f6', // blue-500
  SAM: '#10b981', // emerald-500
  USG: '#f59e0b', // amber-500
  WAF: '#8b5cf6', // violet-500
};

/**
 * Cargo count chart colors
 */
export const CARGO_COUNT_COLORS = {
  fixed: '#0ea5e9', // primary-500 (sky blue)
  expected: '#9ca3af', // gray-400
  average: '#d1d5db', // gray-300
} as const;
